import React, { useState, useEffect, useCallback } from 'react';
import { AdminService } from '../../services/AdminService';

// User interface
interface User {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  aliasName?: string;
  role: 'contributor' | 'moderator' | 'admin';
  subscriptionTier: 'free' | 'individual' | 'group' | 'organization' | 'custom';
  createdAt: string;
  updatedAt: string;
  location?: {
    zipcode?: string;
    city?: string;
    state?: string;
  };
  emailPreferences?: {
    newsletter: boolean;
    notifyOnInspiration: boolean;
  };
}

interface UsersResponse {
  success: boolean;
  data: User[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

/**
 * UserManagement - Component for managing user accounts
 * Allows admins to search, filter, edit users and create moderators
 */
const UserManagement: React.FC = () => {
  // State management
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  
  // Filter and pagination states
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [tierFilter, setTierFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<'createdAt' | 'updatedAt' | 'email' | 'lastName'>('createdAt');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [pageSize] = useState(20);

  // Modal states
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showCreateModerator, setShowCreateModerator] = useState(false);
  
  // Create moderator form state
  const [moderatorForm, setModeratorForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    subscriptionTier: 'individual' as 'individual' | 'group' | 'organization' | 'custom'
  });

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const filters: any = {
        page: currentPage,
        limit: pageSize,
        sort: sortBy
      };
      
      if (searchTerm.trim()) filters.search = searchTerm.trim();
      if (roleFilter) filters.role = roleFilter;
      if (tierFilter) filters.tier = tierFilter;

      const response: UsersResponse = await AdminService.getUsers(filters);
      
      if (response.success) {
        setUsers(response.data);
        setTotalPages(response.pagination.pages);
        setTotalUsers(response.pagination.total);
      } else {
        throw new Error('Failed to load users');
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, roleFilter, tierFilter, sortBy, currentPage, pageSize]);

  // Effect to fetch data when filters or page change
  useEffect(() => {
    fetchUsers();
  }, [searchTerm, roleFilter, tierFilter, sortBy, currentPage, fetchUsers]);

  // Handle user profile update
  const handleUpdateUser = async (userId: string, updatedData: any) => {
    try {
      setSaving(true);
      await AdminService.updateUserProfile(userId, updatedData);
      
      // Update the user in the local state
      setUsers(users.map(user => 
        user._id === userId ? { ...user, ...updatedData } : user
      ));
      
      setEditingUser(null);
      alert('User updated successfully');
    } catch (err) {
      console.error('Error updating user:', err);
      alert(err instanceof Error ? err.message : 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  // Handle moderator creation
  const handleCreateModerator = async () => {
    try {
      setSaving(true);
      await AdminService.createModerator(moderatorForm);
      
      // Reset form and close modal
      setModeratorForm({
        email: '',
        firstName: '',
        lastName: '',
        subscriptionTier: 'individual'
      });
      setShowCreateModerator(false);
      
      // Refresh users list
      await fetchUsers();
      
      alert('Moderator created successfully');
    } catch (err) {
      console.error('Error creating moderator:', err);
      alert(err instanceof Error ? err.message : 'Failed to create moderator');
    } finally {
      setSaving(false);
    }
  };

  // Format date for display
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get role badge styling
  const getRoleBadge = (role: string) => {
    let badgeClass = 'badge ';
    
    switch (role) {
      case 'admin':
        badgeClass += 'bg-danger';
        break;
      case 'moderator':
        badgeClass += 'bg-warning text-dark';
        break;
      case 'contributor':
        badgeClass += 'bg-primary';
        break;
      default:
        badgeClass += 'bg-secondary';
    }
    
    return <span className={badgeClass}>{role}</span>;
  };

  // Get tier badge styling
  const getTierBadge = (tier: string) => {
    let badgeClass = 'badge ';
    
    switch (tier) {
      case 'free':
        badgeClass += 'bg-light text-dark';
        break;
      case 'individual':
        badgeClass += 'bg-info';
        break;
      case 'group':
        badgeClass += 'bg-success';
        break;
      case 'organization':
        badgeClass += 'bg-primary';
        break;
      case 'custom':
        badgeClass += 'bg-dark';
        break;
      default:
        badgeClass += 'bg-secondary';
    }
    
    return <span className={badgeClass}>{tier}</span>;
  };

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1 className="h3 mb-1">User Management</h1>
              <p className="text-muted mb-0">
                Manage user accounts, roles, and subscription tiers
              </p>
            </div>
            <button 
              className="btn btn-primary"
              onClick={() => setShowCreateModerator(true)}
            >
              <i className="fas fa-plus me-2"></i>
              Create Moderator
            </button>
          </div>
        </div>
      </div>

      {/* Filters Row */}
      <div className="row mb-4">
        <div className="col-md-4">
          <label htmlFor="searchTerm" className="form-label">Search Users</label>
          <input
            type="text"
            id="searchTerm"
            className="form-control"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="col-md-2">
          <label htmlFor="roleFilter" className="form-label">Role</label>
          <select
            id="roleFilter"
            className="form-select"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="">All Roles</option>
            <option value="contributor">Contributor</option>
            <option value="moderator">Moderator</option>
            <option value="admin">Admin</option>
          </select>
        </div>

        <div className="col-md-2">
          <label htmlFor="tierFilter" className="form-label">Tier</label>
          <select
            id="tierFilter"
            className="form-select"
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value)}
          >
            <option value="">All Tiers</option>
            <option value="free">Free</option>
            <option value="individual">Individual</option>
            <option value="group">Group</option>
            <option value="organization">Organization</option>
            <option value="custom">Custom</option>
          </select>
        </div>

        <div className="col-md-2">
          <label htmlFor="sortBy" className="form-label">Sort By</label>
          <select
            id="sortBy"
            className="form-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
          >
            <option value="createdAt">Created Date</option>
            <option value="updatedAt">Updated Date</option>
            <option value="email">Email</option>
            <option value="lastName">Last Name</option>
          </select>
        </div>

        <div className="col-md-2 d-flex align-items-end">
          <button className="btn btn-outline-secondary w-100" onClick={fetchUsers}>
            <i className="fas fa-sync-alt me-2"></i>
            Refresh
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="row mb-3">
        <div className="col-12">
          <div className="text-muted">
            Showing {users.length} of {totalUsers} users
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading users...</p>
        </div>
      ) : (
        <>
          {/* Users Table */}
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-striped table-hover">
                      <thead className="table-dark">
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Role</th>
                          <th>Tier</th>
                          <th>Location</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="text-center py-4">
                              No users found matching current filters.
                            </td>
                          </tr>
                        ) : (
                          users.map((user) => (
                            <tr key={user._id}>
                              <td>
                                <div>
                                  <strong>{user.firstName} {user.lastName}</strong>
                                  {user.aliasName && (
                                    <div className="small text-muted">
                                      Alias: {user.aliasName}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td>{user.email}</td>
                              <td>{getRoleBadge(user.role)}</td>
                              <td>{getTierBadge(user.subscriptionTier)}</td>
                              <td>
                                {user.location ? (
                                  <span>
                                    {[user.location.city, user.location.state, user.location.zipcode]
                                      .filter(Boolean)
                                      .join(', ')}
                                  </span>
                                ) : (
                                  <span className="text-muted">Not set</span>
                                )}
                              </td>
                              <td>
                                <small>{formatDate(user.createdAt)}</small>
                              </td>
                              <td>
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => setEditingUser(user)}
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <nav aria-label="User management pagination" className="mt-4">
              <ul className="pagination justify-content-center">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                </li>
                
                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                  return (
                    <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    </li>
                  );
                })}
                
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit User: {editingUser.firstName} {editingUser.lastName}</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setEditingUser(null)}
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target as HTMLFormElement);
                  const updatedData: any = {};
                  
                  formData.forEach((value, key) => {
                    if (key.includes('.')) {
                      // Handle nested properties like location.city
                      const [parent, child] = key.split('.');
                      if (!updatedData[parent]) updatedData[parent] = {};
                      updatedData[parent][child] = value;
                    } else {
                      updatedData[key] = value;
                    }
                  });
                  
                  handleUpdateUser(editingUser._id, updatedData);
                }}>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">First Name</label>
                        <input
                          type="text"
                          className="form-control"
                          name="firstName"
                          defaultValue={editingUser.firstName}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Last Name</label>
                        <input
                          type="text"
                          className="form-control"
                          name="lastName"
                          defaultValue={editingUser.lastName}
                          required
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Alias Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="aliasName"
                      defaultValue={editingUser.aliasName || ''}
                    />
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Role</label>
                        <select
                          className="form-select"
                          name="role"
                          defaultValue={editingUser.role}
                        >
                          <option value="contributor">Contributor</option>
                          <option value="moderator">Moderator</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Subscription Tier</label>
                        <select
                          className="form-select"
                          name="subscriptionTier"
                          defaultValue={editingUser.subscriptionTier}
                        >
                          <option value="free">Free</option>
                          <option value="individual">Individual</option>
                          <option value="group">Group</option>
                          <option value="organization">Organization</option>
                          <option value="custom">Custom</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label">City</label>
                        <input
                          type="text"
                          className="form-control"
                          name="location.city"
                          defaultValue={editingUser.location?.city || ''}
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label">State</label>
                        <input
                          type="text"
                          className="form-control"
                          name="location.state"
                          defaultValue={editingUser.location?.state || ''}
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label">Zip Code</label>
                        <input
                          type="text"
                          className="form-control"
                          name="location.zipcode"
                          defaultValue={editingUser.location?.zipcode || ''}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="d-flex justify-content-end gap-2">
                    <button 
                      type="button" 
                      className="btn btn-secondary" 
                      onClick={() => setEditingUser(null)}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Moderator Modal */}
      {showCreateModerator && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Create New Moderator</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowCreateModerator(false)}
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={(e) => {
                  e.preventDefault();
                  handleCreateModerator();
                }}>
                  <div className="mb-3">
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      className="form-control"
                      value={moderatorForm.email}
                      onChange={(e) => setModeratorForm({...moderatorForm, email: e.target.value})}
                      required
                    />
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">First Name *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={moderatorForm.firstName}
                          onChange={(e) => setModeratorForm({...moderatorForm, firstName: e.target.value})}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Last Name *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={moderatorForm.lastName}
                          onChange={(e) => setModeratorForm({...moderatorForm, lastName: e.target.value})}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Subscription Tier *</label>
                    <select
                      className="form-select"
                      value={moderatorForm.subscriptionTier}
                      onChange={(e) => setModeratorForm({...moderatorForm, subscriptionTier: e.target.value as any})}
                      required
                    >
                      <option value="individual">Individual</option>
                      <option value="group">Group</option>
                      <option value="organization">Organization</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>

                  <div className="d-flex justify-content-end gap-2">
                    <button 
                      type="button" 
                      className="btn btn-secondary" 
                      onClick={() => setShowCreateModerator(false)}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                      {saving ? 'Creating...' : 'Create Moderator'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement; 